import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import { MessageContent, MessageInput, isMessageDocEmpty } from "components/atoms/message";
import Text from "components/atoms/text/Text";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background-color: var(--color-White);
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 12px 12px 6px 12px;
`;

const HeaderTitle = styled(Text).attrs({
    variant: "body-lg-500",
    color: "var(--color-SubBlack)",
})``;

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 0 12px 0;
`;

const Item = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 32px;
    margin: 2px 8px;
    padding: 6px 10px 6px 12px;
    border: 0;
    border-radius: 6px;
    background-color: ${({ $active }) => ($active ? "var(--color-Base2)" : "transparent")};
    cursor: pointer;

    &:hover {
        background-color: ${({ $active }) => ($active ? "var(--color-Base2)" : "var(--color-ListHover)")};
    }

    &:hover .thread-list-item-more,
    &:focus-within .thread-list-item-more {
        opacity: 1;
        pointer-events: auto;
    }

    &:hover .review-thread-list-item-title,
    &:focus-within .review-thread-list-item-title {
        padding-right: 28px;
    }

    ${({ $menuOpen }) =>
        $menuOpen
            ? `
        .review-thread-list-item-title {
            padding-right: 28px;
        }
    `
            : ""}
`;

const ItemTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: padding-right 100ms ease-in;
`;

const ItemTitleInput = styled(MessageInput)`
    flex: 1;
    min-width: 0;
    border-radius: 4px;
    box-shadow: none;

    .ProseMirror {
        min-height: 20px;
        max-height: 20px;
        padding: 0;
        overflow: hidden;
        white-space: nowrap;
        font-size: 1.3rem;
        line-height: 20px;
    }
`;

const MoreButton = styled.button`
    position: absolute;
    top: 50%;
    right: 0px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
    transition: opacity 100ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const ContextMenu = styled.div`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    z-index: 1000;
    width: 128px;
    padding: 4px;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    background-color: var(--color-White);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
`;

const ContextMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 28px;
    padding: 0 8px;
    border: 0;
    border-radius: 3px;
    background-color: transparent;
    color: ${({ $danger }) => ($danger ? "var(--color-Warn)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const EmptyText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    padding: 24px 12px;
    text-align: center;
`;

const LoadingRow = styled(EmptyText)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

function ReferenceReviewThreadList({
    threads = [],
    isLoading = false,
    selectedThread = null,
    onSelectThread,
    threadUpdateTitle,
    chatDelete,
    showHeader = true,
    title = "리뷰",
    emptyMessage = "아직 작성된 리뷰가 없습니다. 새 리뷰를 시작해 보세요.",
    exposeLegacyTitleClass = true,
}) {
    const [threadActionMenu, setThreadActionMenu] = useState(null);
    const [editingThreadId, setEditingThreadId] = useState(null);
    const [editingThreadTitle, setEditingThreadTitle] = useState("");
    const threadActionMenuRef = useRef(null);
    const skipThreadTitleCommitRef = useRef(false);

    useEffect(() => {
        if (!threadActionMenu) return;

        const closeMenu = (event) => {
            if (threadActionMenuRef.current?.contains(event.target)) return;
            setThreadActionMenu(null);
        };

        window.addEventListener("mousedown", closeMenu);
        return () => window.removeEventListener("mousedown", closeMenu);
    }, [threadActionMenu]);

    const commitThreadTitleEdit = useCallback(
        (thread, titleValue = editingThreadTitle) => {
            if (skipThreadTitleCommitRef.current) {
                skipThreadTitleCommitRef.current = false;
                return;
            }

            const nextTitle = titleValue.trim();
            setEditingThreadId(null);
            setEditingThreadTitle("");

            if (!nextTitle || isMessageDocEmpty(nextTitle) || nextTitle === thread.title) return;
            threadUpdateTitle?.(thread.id, nextTitle);
        },
        [editingThreadTitle, threadUpdateTitle],
    );

    const visibleThreads = threads.filter((thread) => !thread.is_deleted);

    return (
        <Container>
            {showHeader && (
                <Header>
                    <Icon name="thread" size="18" color="var(--color-SubBlack)" />
                    <HeaderTitle>{title}</HeaderTitle>
                </Header>
            )}
            <Body>
                {isLoading ? (
                    <LoadingRow>
                        <Icon name="load" size="12" color="var(--color-Grey1)" />
                        리뷰 목록을 불러오는 중입니다.
                    </LoadingRow>
                ) : visibleThreads.length === 0 ? (
                    <EmptyText>{emptyMessage}</EmptyText>
                ) : (
                    visibleThreads.map((thread) => (
                        <Item
                            key={thread.id}
                            $active={selectedThread?.id === thread.id}
                            $menuOpen={threadActionMenu?.thread?.id === thread.id}
                            onClick={() => onSelectThread?.(thread)}
                        >
                            {editingThreadId === thread.id ? (
                                <ItemTitleInput
                                    autoFocus
                                    className="thread-list-item-title-input"
                                    value={editingThreadTitle}
                                    onClick={(event) => event.stopPropagation()}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onChange={setEditingThreadTitle}
                                    onSubmit={(nextTitle) => commitThreadTitleEdit(thread, nextTitle)}
                                    onCancel={() => {
                                        skipThreadTitleCommitRef.current = true;
                                        setEditingThreadId(null);
                                        setEditingThreadTitle("");
                                    }}
                                    onBlur={() => commitThreadTitleEdit(thread)}
                                    placeholder="리뷰 제목"
                                    multiline={false}
                                    features={false}
                                    showSubmit={false}
                                />
                            ) : (
                                <ItemTitle
                                    className={
                                        exposeLegacyTitleClass
                                            ? "review-thread-list-item-title thread-list-item-title"
                                            : "review-thread-list-item-title"
                                    }
                                >
                                    <MessageContent content={thread.title || ""} singleLine />
                                </ItemTitle>
                            )}
                            {editingThreadId !== thread.id && (
                                <Tooltip message="더보기">
                                    <MoreButton
                                        type="button"
                                        className="thread-list-item-more"
                                        $visible={threadActionMenu?.thread?.id === thread.id}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const rect = event.currentTarget.getBoundingClientRect();
                                            setThreadActionMenu({
                                                thread,
                                                top: rect.bottom + 4,
                                                left: rect.right - 128,
                                            });
                                        }}
                                    >
                                        <Icon name="submenu" size="14" color="var(--color-Grey1)" />
                                    </MoreButton>
                                </Tooltip>
                            )}
                        </Item>
                    ))
                )}
            </Body>
            {threadActionMenu && (
                <ContextMenu ref={threadActionMenuRef} $top={threadActionMenu.top} $left={threadActionMenu.left}>
                    <ContextMenuItem
                        type="button"
                        onClick={() => {
                            skipThreadTitleCommitRef.current = false;
                            setEditingThreadId(threadActionMenu.thread.id);
                            setEditingThreadTitle(threadActionMenu.thread.title || "");
                            setThreadActionMenu(null);
                        }}
                    >
                        제목 수정
                    </ContextMenuItem>
                    <ContextMenuItem
                        type="button"
                        $danger
                        onClick={() => {
                            const targetThread = threadActionMenu.thread;
                            setThreadActionMenu(null);
                            Confirm(
                                "warn",
                                "리뷰 삭제",
                                "리뷰를 삭제하면 되돌릴 수 없습니다. 삭제하시겠습니까?",
                                "삭제",
                                () => {
                                    chatDelete?.(targetThread.id, null, true);
                                },
                            );
                        }}
                    >
                        리뷰 삭제
                    </ContextMenuItem>
                </ContextMenu>
            )}
        </Container>
    );
}

export default ReferenceReviewThreadList;
